import { useMemo } from "react";
import { Button, Card, Popconfirm, Tag } from "@arco-design/web-react";
import { Pencil, Trash2 } from "lucide-react";

import "./MySqlQueryListPage.css";
import {
  MySqlQueryFilterCard,
  MySqlQueryRecordModal,
  MySqlQueryResultTable,
  useMySqlQueryListDemo2Runtime,
  type MySqlQueryDisplayColumn,
  type MySqlQueryListItem,
} from "../mysql-query-list";

/** MySQL 分页查询列表 Demo2 页面。 */
export function MySqlQueryListDemo2Page() {
  const runtime = useMySqlQueryListDemo2Runtime();

  const columns = useMemo<MySqlQueryDisplayColumn<MySqlQueryListItem>[]>(
    () => [
      {
        key: "actions",
        title: "操作",
        align: "center",
        render: (item) => (
          <div className="mysql-query-row-actions">
            <Button
              type="text"
              shape="circle"
              className="mysql-query-row-action-button"
              aria-label="编辑"
              title="编辑"
              icon={<Pencil size={16} strokeWidth={2} />}
              onClick={() => runtime.handleOpenEditModal(item)}
            />
            <Popconfirm
              title="确认删除该记录？"
              content="删除后将标记删除，列表默认不再展示。"
              okText="确认删除"
              cancelText="取消"
              okType="primary"
              onOk={() => runtime.handleConfirmDelete(item)}
              okButtonProps={{ status: "danger", loading: runtime.deleteSubmitting }}
              cancelButtonProps={{ disabled: runtime.deleteSubmitting }}
              trigger="click"
              unmountOnExit
            >
              <Button
                type="text"
                shape="circle"
                status="danger"
                className="mysql-query-row-action-button is-danger"
                aria-label="删除"
                title="删除"
                icon={<Trash2 size={16} strokeWidth={2} />}
              />
            </Popconfirm>
          </div>
        ),
      },
      {
        key: "recordName",
        title: "记录名称",
        align: "left",
        render: (item) => <span className="mysql-query-record-name-text">{item.recordName}</span>,
      },
      {
        key: "sourceLabel",
        title: "数据来源",
        align: "left",
        render: (item) => item.sourceLabel,
      },
      {
        key: "tagsLabel",
        title: "标签",
        align: "left",
        render: (item) => (
          <div className="mysql-query-table-tag-list">
            {item.tagsLabel.map((tag) => (
              <Tag key={tag} color="arcoblue">
                {tag}
              </Tag>
            ))}
          </div>
        ),
      },
      {
        key: "bizDate",
        title: "业务日期",
        align: "center",
        render: (item) => item.bizDate,
      },
      {
        key: "amount",
        title: "金额",
        align: "right",
        render: (item) => runtime.formatAmount(item.amount),
      },
      {
        key: "enabledLabel",
        title: "是否启用",
        align: "center",
        render: (item) => (
          <span className={item.enabled ? "mysql-query-status-pill is-enabled" : "mysql-query-status-pill"}>
            {item.enabledLabel}
          </span>
        ),
      },
      {
        key: "createdAt",
        title: "创建时间",
        align: "center",
        render: (item) => item.createdAt,
      },
      {
        key: "updatedAt",
        title: "更新时间",
        align: "center",
        render: (item) => item.updatedAt,
      },
    ],
    [runtime],
  );

  return (
    <main className="mysql-query-page">
      <Card className="mysql-query-card" bodyStyle={{ padding: 20 }}>
        <h2 style={{ margin: 0, color: "#1d2129", fontSize: 20, fontWeight: 700 }}>通用演示列表 Demo2</h2>
      </Card>

      <MySqlQueryFilterCard
        formState={runtime.formState}
        isCompact={runtime.isCompact}
        showAdvancedFilters={runtime.showAdvancedFilters}
        errorMessage={runtime.displayErrorMessage}
        sourceOptions={runtime.sourceOptions}
        tagOptions={runtime.tagOptions}
        setFormState={runtime.setFormState}
        onToggleAdvancedFilters={() => runtime.setShowAdvancedFilters((current) => !current)}
        onReset={runtime.handleReset}
        onSearch={runtime.handleSearch}
      />

      <Card className="mysql-query-card" bodyStyle={{ padding: 0 }}>
        <MySqlQueryResultTable
          columns={columns}
          items={runtime.items}
          loading={runtime.loading}
          pageSizeSelectOptions={runtime.pageSizeSelectOptions}
          onOpenCreateModal={runtime.handleOpenCreateModal}
          onPageChange={runtime.handlePageChange}
          onPageSizeChange={runtime.handlePageSizeChange}
          page={runtime.currentPage}
          pageSize={runtime.currentPageSize}
          paginationItems={runtime.paginationItems}
          total={runtime.total}
          totalPages={runtime.totalPages}
        />
      </Card>

      {runtime.recordModalVisible ? (
        <MySqlQueryRecordModal
          visible={runtime.recordModalVisible}
          mode={runtime.recordModalMode}
          compact={runtime.viewportMode === "mobile"}
          submitting={runtime.recordSubmitting}
          initialValues={runtime.recordModalInitialValues}
          sourceOptions={runtime.sourceOptions}
          tagOptions={runtime.tagOptions}
          onCancel={runtime.handleCloseRecordModal}
          onSubmit={runtime.handleSubmitRecordModal}
        />
      ) : null}
    </main>
  );
}
